<template>
  <div v-loading="isLoading">
    <el-table
      ref="fuelsTable"
      :data="surcharges"
      stripe
    >
      <el-table-column type="expand">
        <template slot-scope="props">
          <template v-if="!!props.row.rule">
            <h3>
              Surcharge&nbsp;Rule:&nbsp;<span class="font-weight-bold">{{ props.row.rule.desc || '-' }}&nbsp;-&nbsp;{{ props.row.rule.on || '-' }}</span>
            </h3>
            <code>{{ props.row.rule.expression || '-' }}</code>
          </template>

          <template v-else>
            <p>This Surcharge has no associated Rules</p>
          </template>
        </template>
      </el-table-column>

      <el-table-column
        label="Name"
        prop="name"
        sortable
        min-width="260px"
        show-overflow-tooltip
      >
        <template slot-scope="scope">
          {{ scope.row.name || '-' }}

          <el-tag
            v-if="scope.row.audit"
            size="mini"
            type="danger"
          >
            Auditing
          </el-tag>
          <i
            v-if="scope.row.notes && scope.row.notes.length > 0"
            class="el-icon-potato-strips ml-2"
            title="This surcharge has notes"
          />
        </template>
      </el-table-column>

      <el-table-column
        label="Short"
        prop="short"
        min-width="180px"
        show-overflow-tooltip
        :formatter="(row, col, cellValue) => cellValue ? (cellValue || '-') : '-' "
      />

      <el-table-column
        label="External Name"
        prop="ext_name"
        min-width="260px"
        show-overflow-tooltip
        :formatter="(row, col, cellValue) => cellValue ? (cellValue || '-') : '-' "
      />

      <el-table-column
        label="Carrier"
        sortable
        width="180px"
        show-overflow-tooltip
      >
        <template slot-scope="scope">
          <span v-if="scope.row.carrier">
            {{ scope.row.carrier.name || '-' }}
          </span>

          <el-tag
            v-else
            type="warning"
            size="small"
          >
            Invoice Fee
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column
        label="Started At"
        prop="date_start"
        sortable
        width="120px"
        :formatter="(row, col, cellValue) => (cellValue && cellValue !== 'Invalid date') ? $moment(cellValue).format('DD/MM/YYYY') : '-'"
      />

      <el-table-column
        label="Ended At"
        prop="date_end"
        width="120px"
        :formatter="(row, col, cellValue) => (cellValue && cellValue !== 'Invalid date') ? $moment(cellValue).format('DD/MM/YYYY') : '-'"
      />

      <el-table-column
        v-if="visibility.deleted_date"
        label="Deleted At"
        prop="deleted_date"
        :formatter="(row, col, cellValue) => cellValue ? $moment(cellValue).format('DD/MM/YYYY') : '-'"
      />

      <el-table-column
        label="Buy"
        width="120px"
      >
        <template slot-scope="scope">
          {{ scope.row.buy_value || '0' }}

          <FontAwesomeIcon
            v-if="scope.row.buy_has_fuel"
            class="ml-1 text-danger"
            :icon="[ 'fal', 'gas-pump' ]"
            fixed-width
          />
        </template>
      </el-table-column>

      <el-table-column
        label="Sell"
        width="120px"
      >
        <template slot-scope="scope">
          {{ scope.row.sell_value || '0' }}

          <FontAwesomeIcon
            v-if="scope.row.sell_has_fuel"
            class="ml-1 text-danger"
            :icon="[ 'fal', 'gas-pump' ]"
            fixed-width
          />
        </template>
      </el-table-column>

      <el-table-column
        label="Status"
        align="center"
        width="120px"
      >
        <template slot-scope="scope">
          <el-tag
            v-if="scope.row.active"
            type="success"
            size="small"
          >
            Active
          </el-tag>

          <el-tag
            v-else
            type="warning"
            size="small"
          >
            Inactive
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column
        label="Actions"
        align="right"
        width="270px"
      >
        <template slot-scope="scope">
          <el-button
            v-if="canUpdate"
            size="mini"
            @click.native="editSurcharge(scope.$index, scope.row)"
          >
            Edit
          </el-button>

          <el-button
            v-if="canDelete"
            size="mini"
            type="danger"
            @click.native="deleteSurcharge(scope.$index, scope.row)"
          >
            Delete
          </el-button>

          <el-dropdown style="margin-left: 10px;">
            <el-button size="mini">
              <FontAwesomeIcon :icon="['far', 'ellipsis-h']" />
            </el-button>

            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item
                v-if="canUpdate"
                :disabled="!scope.row.carrier"
                @click.native="linkedAccounts(scope.$index, scope.row)"
              >
                Accounts
              </el-dropdown-item>

              <el-dropdown-item
                v-if="canUpdate"
                :disabled="!scope.row.carrier"
                @click.native="linkedServices(scope.$index, scope.row)"
              >
                Services
              </el-dropdown-item>

              <el-dropdown-item
                v-if="canCreate"
                @click.native="duplicateSurcharge(scope.$index, scope.row)"
              >
                Duplicate
              </el-dropdown-item>

              <!-- <el-dropdown-item @click.native="history(scope.$index, scope.row)">
                History
              </el-dropdown-item> -->
              <el-dropdown-item @click.native="notes(scope.$index, scope.row)">
                Notes
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </template>
      </el-table-column>
    </el-table>

    <!-- Pagination -->
    <el-pagination
      class="text-center pt-3"
      :current-page.sync="page"
      :page-sizes="[10, 20, 50, 100]"
      :page-size.sync="perPage"
      layout="total, sizes, prev, pager, next, jumper"
      :total="total"
      @size-change="loadTable"
      @current-change="loadTable"
    />

    <!-- Visibility Form -->
    <TableColumnsVisibility
      v-if="isShowVisibilityDialog"
      ref="visibilityForm"
      v-model="visibility"
      :is-show.sync="isShowVisibilityDialog"
    />

    <!-- Edit Form -->
    <SurchargeEditForm
      v-if="isShowEditForm"
      ref="editForm"
      :is-show.sync="isShowEditForm"
      :surcharge="surcharge"
      @saved="loadTable"
    />

    <!-- Linked Accounts -->
    <SurchargeLinkedAccountsForm
      v-if="isShowLinkedAccountsForm"
      ref="linkedAccountsForm"
      :is-show.sync="isShowLinkedAccountsForm"
      :group="group"
    />

    <!-- Linked Services -->
    <SurchargeLinkedServicesForm
      v-if="isShowLinkedServicesForm"
      ref="linkedServicesForm"
      :is-show.sync="isShowLinkedServicesForm"
      :surcharge="surcharge"
    />

    <!-- Duplicate Form -->
    <SurchargeDuplicateForm
      v-if="isShowDuplicateForm"
      ref="duplicateForm"
      :is-show.sync="isShowDuplicateForm"
      :old-surcharge="surcharge"
    />
  </div>
</template>

<script>
import Vue from 'vue'
import TableColumnsVisibility from '@/components/Application/Manage/TableColumnsVisibility'
import SurchargeEditForm from '@/components/Application/Manage/Surcharges/SurchargeEditForm'
import SurchargeLinkedAccountsForm from '@/components/Application/Manage/Surcharges/SurchargeLinkedAccountsForm'
import SurchargeLinkedServicesForm from '@/components/Application/Manage/Surcharges/SurchargeLinkedServicesForm'
import SurchargeDuplicateForm from '@/components/Application/Manage/Surcharges/SurchargeDuplicateForm'
import SurchargeNotesDialog from '@/components/Application/Manage/Surcharges/SurchargeNotesDialog'
import Filter, { IS } from '@/plugins/sdk/Filter'
import { CREATE, UPDATE, DELETE } from '@/plugins/user'

export default {
  components: {
    TableColumnsVisibility,
    SurchargeEditForm,
    SurchargeLinkedAccountsForm,
    SurchargeLinkedServicesForm,
    SurchargeDuplicateForm
  },
  props: {
    filters: {
      type: Array,
      required: true
    },
    sort: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      actions: [
        {
          hidden: this.$user.cannot(CREATE, this.$sdk.ratesurcharge),
          name: 'Create',
          icon: ['fal', 'plus'],
          event: () => {
            this.surcharge = new this.$sdk.ratesurcharge()
            this.isShowEditForm = true
          }
        },
        {
          name: 'Refresh',
          icon: ['fal', 'sync'],
          event: this.loadTable
        }
      ],
      surcharge: null,
      surcharges: [],
      page: 1,
      perPage: 20,
      total: 0,
      group: null,
      isLoading: true,
      isShowVisibilityDialog: false,
      isShowEditForm: false,
      isShowLinkedAccountsForm: false,
      isShowLinkedServicesForm: false,
      isShowDuplicateForm: false,
      notesDialogInstance: undefined,
      visibility: {
        group: false,
        id: false,
        name: true,
        date_start: true,
        date_end: false,
        carrier: true,
        buy: true,
        sell: true,
        active: true,
        deleted_date: false
      }
    }
  },
  computed: {
    canCreate () {
      return this.$user.can(CREATE, this.$sdk.ratesurcharge)
    },
    canUpdate () {
      return this.$user.can(UPDATE, this.$sdk.ratesurcharge)
    },
    canDelete () {
      return this.$user.can(DELETE, this.$sdk.ratesurcharge)
    }
  },
  beforeDestroy () {
    if (typeof this.notesDialogInstance !== 'undefined') {
      this.notesDialogInstance.$destroy()
      this.notesDialogInstance.$el.remove()
      this.notesDialogInstance = undefined
    }
  },
  methods: {
    loadTable () {
      this.isLoading = true
      this.$sdk.ratesurcharge.list(builder => {
        builder.include = ['group', 'carrier', 'rule', 'notes']
        builder.page = this.page
        builder.limit = this.perPage
        builder.order = `${this.sort.field}:${this.sort.direction}`
        builder.filters = this.filters
      })
        .then(res => {
          this.surcharges = res
          this.total = res.total
        })
        .finally(() => { this.isLoading = false })
    },
    linkedAccounts (index, row) {
      this.group = row.group.clone()
      this.isShowLinkedAccountsForm = true
    },
    linkedServices (index, row) {
      this.surcharge = row.clone()
      this.isShowLinkedServicesForm = true
    },
    duplicateSurcharge (index, row) {
      this.surcharge = row.clone()
      this.isShowDuplicateForm = true
    },
    history (index, row) {
      const existedGroupFilterIndex = this.filters.findIndex(f => f.field === 'group')
      if (existedGroupFilterIndex !== -1) {
        this.filters[existedGroupFilterIndex].value = row.group.id
      } else {
        this.filters.push(new Filter('group', IS, row.group.id))
      }
      this.loadTable()
    },
    editSurcharge (index, row) {
      this.surcharge = row.clone()
      this.isShowEditForm = true
    },
    notes (index, row) {
      if (typeof this.notesDialogInstance !== 'undefined') {
        this.notesDialogInstance.$destroy()
        this.notesDialogInstance.$el.remove()
        this.notesDialogInstance = undefined
      }

      const SurchargeNotesDialogClass = Vue.extend(SurchargeNotesDialog)

      this.notesDialogInstance = new SurchargeNotesDialogClass({
        propsData: {
          surchargeId: row.id
        }
      })

      this.notesDialogInstance.$on('destroy', () => {
        this.notesDialogInstance.$destroy()
        this.notesDialogInstance.$el.remove()
        this.notesDialogInstance = undefined
      })

      this.notesDialogInstance.$mount()
      this.$root.$el.appendChild(this.notesDialogInstance.$el)
    },
    deleteSurcharge (index, row) {
      this.$confirm('Are you sure to delete this surcharge?', 'Delete', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      })
        .then(() => {
          this.isLoading = true
          row
            .clone()
            .delete()
            .then(() =>
              this.$message({
                message: 'Successfully deleted',
                type: 'success'
              })
            )
            .catch(err => {
              this.$message.error('An error occurred while deleting')
              throw err
            })
            .finally(() => {
              this.loadTable()
            })
        })
        .catch(() => {})
    }
  }
}
</script>

<style>
</style>
